import { inject, Injectable, Injector } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { IPopupModel } from '@mwe/models';
import { DEFAULT_POPUP_COMPONENT_TOKEN } from '@mwe/constants';
import { isMobile } from '@mwe/utils';

@Injectable({
  providedIn: 'root',
})
export class IkpPopupService {
  private readonly modalService = inject(NgbModal);
  private readonly injector = inject(Injector);

  private _events: Subject<unknown>;

  private _additionalButtonClickEvent: Subject<string>;
  private cleanUpSubscriptions = true;
  private popupsDisabled = false;

  constructor() {
    this._events = new Subject();
    this._additionalButtonClickEvent = new Subject();
  }

  public handleGenericPopup(data: IPopupModel, closeOpenPopups = false): Promise<unknown> {
    let component = this.injector.get(DEFAULT_POPUP_COMPONENT_TOKEN);
    if (this.modalService.hasOpenModals()) {
      if (closeOpenPopups) {
        this.modalService.dismissAll();
      } else {
        throw new Error("modal already open - can't open more than one");
      }
    }

    const modalOptions: NgbModalOptions = {
      backdrop: data.dismissable ? true : 'static',
      keyboard: data.dismissable,
    };

    // Set mobile view properties and other modal options
    this.setMobileViewProperties(data, modalOptions);
    this.setModalOptions(data, modalOptions);

    if (data.component) {
      component = data.component;
    }

    return this.openModal(component, data, modalOptions);
  }

  /**
   * Creates an error popup with the default error message if no other popups are open.
   * Can be configured with custom title- and error-description keys
   */
  public handleErrorPopup(id: string, titleKey: string, descriptionKey = 'global.errors.defaultErrorMessage', closeOpenPopups = false) {
    const model = {
      id: id,
      titleKey: titleKey,
      messageKey: descriptionKey,
      showSubmitButton: true,
      submitButtonKey: 'OK',
      showCancelButton: false,
      iconColorClass: 'red',
      iconTypeClass: 'fa-bell',
    };
    this.handleGenericPopup(model, closeOpenPopups);
  }

  public dismissAllPopups() {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
  }

  public disablePopups() {
    this.popupsDisabled = true;
    this.dismissAllPopups();
  }

  //Feature to enable the popups again, if disabled via disablePopups(). Not used yet but part of the feature's API.
  public enablePopups() {
    this.popupsDisabled = false;
  }

  private setMobileViewProperties(data: IPopupModel, modalOptions: NgbModalOptions) {
    if (isMobile()) {
      modalOptions.windowClass = `${data.disableMobileView === true ? '' : 'modal-mobile'} animated slideInUp faster`;
    } else {
      modalOptions.centered = true;
    }
  }

  private setModalOptions(data: IPopupModel, modalOptions: NgbModalOptions): void {
    if (data.modalSize) {
      modalOptions.size = data.modalSize;
    }
    modalOptions.centered = true;
  }

  private async openModal(component: unknown, data: IPopupModel, modalOptions: NgbModalOptions): Promise<unknown> {
    if (this.popupsDisabled) {
      console.warn('Popups are disabled, call ikpPopupService.enablePopups() before trying to open a popup.');
      return null;
    }
    const popupRef = this.modalService.open(component, modalOptions);
    popupRef.componentInstance.model = data;
    if (data.componentData) {
      popupRef.componentInstance.loadedData = data.componentData;
    }
    const additionalButtonClickSub = popupRef.componentInstance.onAdditionalButtonClick?.subscribe(buttonId => {
      this._additionalButtonClickEvent.next(buttonId);
    });

    try {
      const result = await popupRef.result;
      const resultValue = data.formModel || data.useCustomReturnValue ? result : result === 'OK';
      this.events.next(resultValue);
      return resultValue;
    } catch {
      this.events.next(false);
      return false;
    } finally {
      if (this.cleanUpSubscriptions) {
        this.unsubscribeAll(additionalButtonClickSub);
      }
    }
  }

  public unsubscribeAll(additionalButtonClickSub: Subscription) {
    this._events.complete();
    this._events = new Subject();
    additionalButtonClickSub?.unsubscribe();
    this._additionalButtonClickEvent.complete();
    this._additionalButtonClickEvent = new Subject();
    this.cleanUpSubscriptions = true;
  }

  public get events(): Subject<unknown> {
    return this._events;
  }
}
